import { useMemo } from "react";
import { CalendarDays, Clock3, Copy, ExternalLink, Users2, X } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/Avatar";
import { Card } from "@/components/Card";
import { Heading, Text } from "@/components/Typography";
import { NormalButton } from "@/components/NormalButton";
import { Tooltip } from "@/components/Tooltip";
import { useToast } from "@/hooks/useToast";
import { AdvisorMeeting, MeetingParticipant } from "@/services/api";
import { getInitials, validateSafeUrl } from "@/utils/util";
import { formatDisplayDate, parseDateInput } from "@/utils/formatDisplayDate";
import { PersonalAdvisoryIcon } from "@/components/icons/icons";

/**
 * Exactly the fields this drawer reads — not the whole AdvisorMeeting.
 *
 * Narrowed so the same drawer can render an Expert Program session prep, which
 * comes from a different endpoint and has no AdvisorMeeting to hand. An
 * AdvisorMeeting still satisfies this structurally, so the PAB caller is
 * unchanged.
 */
export type SessionPrepDrawerParticipant = Pick<
  MeetingParticipant,
  "id" | "fullName" | "profileUrl" | "role"
>;

export type SessionPrepDrawerMeeting = Pick<
  AdvisorMeeting,
  | "title"
  | "startTime"
  | "endTime"
  | "meetingFormat"
  | "meetingUrl"
  | "meetingAgendas"
  | "sessionPrepGoals"
> & { participants: SessionPrepDrawerParticipant[] };

export interface SessionPrepDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  meeting: SessionPrepDrawerMeeting | null;
  /**
   * Who authored the briefing, for the "Viewing Session Prep by …" banner.
   *
   * Defaults to the ACHIEVER participant, which is right for a PAB — there the
   * achiever prepares it. On an Expert Program the author is an expert, so that
   * surface passes the name explicitly rather than letting it be inferred from
   * a role that no longer means "the person who wrote this".
   */
  preparedByName?: string | null;
}

interface SectionCardProps {
  title: string;
  children: React.ReactNode;
  className?: string;
  headingClassName?: string;
}

const SectionCard = ({ title, children, className, headingClassName }: SectionCardProps) => (
  <Card
    className={`rounded-xl border border-surface-border bg-surface-card p-5 shadow-sm ${className ?? ""}`}
  >
    <Heading
      as="h3"
      className={`mb-4 font-inter text-18 font-semibold text-goals-create-title ${headingClassName ?? ""}`}
    >
      {title}
    </Heading>
    {children}
  </Card>
);

const getMeetingTypeLabel = (meeting: SessionPrepDrawerMeeting): string => {
  if (meeting.meetingFormat === "GROUP") return "Group Session";
  return /mid/i.test(meeting.title) ? "Mid-Session 1:1" : "1:1 Session";
};

export const SessionPrepDrawer = ({
  isOpen,
  onClose,
  meeting,
  preparedByName,
}: SessionPrepDrawerProps) => {
  const { showSuccessToast, showErrorToast } = useToast();

  const achiever = useMemo(
    () => meeting?.participants.find((participant) => participant.role === "ACHIEVER"),
    [meeting],
  );

  const sortedParticipants = useMemo(() => {
    if (!meeting?.participants?.length) return [];
    return [...meeting.participants].sort((a, b) => {
      if (a.role === b.role) return a.fullName.localeCompare(b.fullName);
      return a.role === "ACHIEVER" ? -1 : 1;
    });
  }, [meeting]);

  const totalAgendaDuration = useMemo(
    () =>
      (meeting?.meetingAgendas ?? []).reduce((acc, item) => {
        const duration = Number.isFinite(item.duration) ? Math.max(0, item.duration) : 0;
        return acc + duration;
      }, 0),
    [meeting],
  );

  if (!isOpen) return null;

  const formattedDate = formatDisplayDate(meeting?.startTime, {
    variant: "date",
    length: "long",
    fallback: "Date TBD",
  });
  const formattedTime =
    parseDateInput(meeting?.startTime) && parseDateInput(meeting?.endTime)
      ? formatDisplayDate(meeting?.startTime, { variant: "time", end: meeting?.endTime })
      : "Time TBD";

  const handleCopyInvite = async () => {
    if (!meeting?.meetingUrl) return;
    try {
      await navigator.clipboard.writeText(meeting.meetingUrl);
      showSuccessToast("Invite copied to clipboard", "Invite copied");
    } catch {
      showErrorToast("Failed to copy invite", "Copy failed");
    }
  };

  const handleOpenInvite = () => {
    const safeUrl = validateSafeUrl(meeting?.meetingUrl);
    if (!safeUrl) return;
    window.open(safeUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/50 transition-opacity" onClick={onClose} />

      <div
        className="absolute right-0 top-0 h-full w-full max-w-[600px] overflow-hidden bg-surface-page-bg shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-prep-title"
      >
        <div className="flex h-full flex-col font-inter">
          <div className="flex shrink-0 items-center justify-between border-b border-surface-border bg-surface-card px-6 py-4">
            <Heading
              as="h2"
              className="font-inter text-20 font-bold leading-7 tracking-[-0.45px] text-goals-create-title"
            >
              Session Prep
            </Heading>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 text-theme-text-muted transition-colors hover:bg-surface-card-inner"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            {!meeting ? (
              <Text as="p" className="text-sm text-theme-text-muted">
                No session selected.
              </Text>
            ) : (
              <div className="space-y-5">
                <div className="rounded-lg border border-accent-450 bg-accent-350 px-4 py-3 dark:border-timeline-session-border dark:bg-timeline-session-bg">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-150 dark:bg-[rgba(59,130,246,0.2)]">
                      <PersonalAdvisoryIcon className="h-4 w-4 text-timeline-session-icon" />
                    </div>
                    <div>
                      <Text as="p" className="text-sm font-semibold text-theme-text-heading">
                        Viewing Session Prep by{" "}
                        {preparedByName ?? achiever?.fullName ?? "Achiever"}
                      </Text>
                      <Text as="p" className="text-xs text-theme-text-secondary">
                        You&apos;re viewing a read-only version of this session prep
                      </Text>
                    </div>
                  </div>
                </div>

                <SectionCard title="Session Details" className="text-18 text-goals-create-title">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs text-theme-text-muted">
                        <CalendarDays className="h-4 w-4 text-timeline-session-icon" />
                        <Text as="span" className="text-12 font-inter">
                          Date
                        </Text>
                      </div>
                      <Text
                        as="p"
                        className="text-14 font-normal text-goals-create-title font-inter"
                      >
                        {formattedDate}
                      </Text>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs text-theme-text-muted">
                        <Clock3 className="h-4 w-4 text-goals-purple-selected" />
                        <Text as="span" className="text-12 font-inter">
                          Time
                        </Text>
                      </div>
                      <Text
                        as="p"
                        className="text-14 font-normal text-goals-create-title font-inter"
                      >
                        {formattedTime}
                      </Text>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs text-theme-text-muted">
                        <Users2 className="h-4 w-4 text-confirm-completion-success-text" />
                        <Text as="span" className="text-12">
                          Type
                        </Text>
                      </div>
                      <Text
                        as="p"
                        className="text-14 font-normal text-goals-create-title font-inter"
                      >
                        {getMeetingTypeLabel(meeting)}
                      </Text>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard title="Participants" className="text-18 text-goals-create-title">
                  {sortedParticipants.length === 0 ? (
                    <Text as="p" className="text-sm text-theme-text-muted">
                      No participants found.
                    </Text>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {sortedParticipants.map((participant) => (
                        <div key={participant.id} className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 border border-surface-border">
                            {participant.profileUrl ? (
                              <AvatarImage
                                src={participant.profileUrl}
                                alt={participant.fullName}
                                className="grayscale"
                              />
                            ) : null}
                            <AvatarFallback className="bg-primary-50 text-xs font-semibold text-primary-700">
                              {getInitials(participant.fullName).slice(0, 2)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <Text
                              as="p"
                              className="truncate text-sm font-semibold text-theme-text-heading"
                            >
                              {participant.fullName}
                            </Text>
                            <Text as="p" className="text-xs text-theme-text-muted">
                              {participant.role === "ACHIEVER" ? "Achiever" : "Expert"}
                            </Text>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </SectionCard>

                <SectionCard title="Focus Goals" className="text-18 text-goals-create-title">
                  {(meeting.sessionPrepGoals ?? []).length === 0 ? (
                    <Text as="p" className="text-sm text-theme-text-muted">
                      No focus goals added yet.
                    </Text>
                  ) : (
                    <div className="space-y-3">
                      {meeting.sessionPrepGoals.map((goal, index) => {
                        const goalLabel = `Goal ${index + 1}: ${goal}`;
                        return (
                          <div
                            key={`${goal}-${index}`}
                            className="rounded-xl border border-surface-border bg-surface-card-inner px-4 py-4"
                          >
                            <Tooltip content={goalLabel} variant="dark" side="top">
                              <Text
                                as="p"
                                className="cursor-default truncate text-15 font-semibold text-goals-create-title font-inter"
                              >
                                {goalLabel}
                              </Text>
                            </Tooltip>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </SectionCard>

                <SectionCard title="Session Agenda" className="text-18 text-goals-create-title">
                  {(meeting.meetingAgendas ?? []).length === 0 ? (
                    <Text as="p" className="text-sm text-theme-text-muted">
                      No agenda items added yet.
                    </Text>
                  ) : (
                    <div className="space-y-3">
                      {meeting.meetingAgendas.map((agenda, index) => (
                        <div
                          key={`${agenda.description}-${index}`}
                          className="flex items-center gap-3 rounded-xl border border-surface-border bg-surface-card-inner px-4 py-3"
                        >
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-150 text-xs font-semibold text-timeline-session-icon">
                            {index + 1}
                          </div>
                          <Text
                            as="p"
                            className="flex-1 text-14 font-medium text-theme-text-heading font-inter"
                          >
                            {agenda.description}
                          </Text>
                          <Text
                            as="span"
                            className="text-14 font-normal text-theme-text-muted font-inter"
                          >
                            {agenda.duration} min
                          </Text>
                        </div>
                      ))}

                      <div className="mt-1 flex items-center justify-between rounded-xl border border-accent-450 bg-accent-350 px-4 py-3 dark:border-timeline-session-border dark:bg-timeline-session-bg">
                        <Text as="p" className="text-14 font-medium text-theme-text font-inter">
                          Total Duration
                        </Text>
                        <Text
                          as="p"
                          className="text-18 font-semibold text-timeline-session-icon font-inter"
                        >
                          {totalAgendaDuration} min
                        </Text>
                      </div>
                    </div>
                  )}
                </SectionCard>

                <SectionCard title="Invite Details" className="text-18 text-goals-create-title">
                  <div className="flex gap-2">
                    <NormalButton
                      borderColor="border-primary-300"
                      bgColor="bg-primary-50"
                      textColor="text-primary-700"
                      hoverBgColor="hover:bg-primary-100"
                      size="md"
                      className="h-10 flex-1 rounded-lg dark:bg-[rgba(50,62,225,0.15)] dark:border-[rgba(167,180,255,0.3)] dark:text-primary-300 dark:hover:bg-[rgba(50,62,225,0.25)]"
                      leftIcon={<Copy className="h-4 w-4" />}
                      onClick={handleCopyInvite}
                      disabled={!meeting.meetingUrl}
                    >
                      Copy Invite
                    </NormalButton>
                    <button
                      type="button"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-primary-300 bg-primary-50 text-primary-700 transition-colors hover:bg-primary-100 dark:bg-[rgba(50,62,225,0.15)] dark:border-[rgba(167,180,255,0.3)] dark:text-primary-300 dark:hover:bg-[rgba(50,62,225,0.25)] disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={handleOpenInvite}
                      disabled={!meeting.meetingUrl}
                      aria-label="Open invite link"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </button>
                  </div>
                </SectionCard>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

SessionPrepDrawer.displayName = "SessionPrepDrawer";

export default SessionPrepDrawer;
